import React from "react";
import IeltsLogo from "../../Assets/Images/IeltsLogo.png";
import "../../Assets/Styles/test-art.css";

/**
 * TEST INTRO ARTWORK
 *
 * The animated hero illustrations from the Claude Design "Test Intro" export,
 * ported as components: an answer sheet filling itself in (mock test), a
 * signpost of majors sprouting from a graduation cap (career), and a book
 * with skills rising beside a flight path (IELTS).
 *
 * Pure CSS animation — no library, no images beyond the IELTS mark — so it
 * costs nothing over the static art it replaced. All motion stops under
 * prefers-reduced-motion (see test-art.css).
 */

/** National mock test: an answer sheet completing itself. */
export function ArtMock() {
  return (
    <div className="ti-art" role="img" aria-label="An answer sheet filling itself in, with a 90% score badge">
      <div className="ti-blob" aria-hidden="true"></div>
      <div className="ti-chip ti-chip--clock">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg> 20 min
      </div>
      <div className="ti-sheet">
        <div className="ti-sheet-head">
          <span className="ti-flag"><span></span><span></span><span></span></span> National Test
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`ti-row ${i === 1 ? "ti-row--pick" : ""}`}>
            <span className="ti-row-letter">{"ABCD"[i]}</span>
            <span className="ti-row-bar"></span>
            {i === 1 && (
              <span className="ti-row-check">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              </span>
            )}
          </div>
        ))}
        <div className="ti-progress"><span></span></div>
      </div>
      <div className="ti-medal">90%</div>
      <div className="ti-cube ti-cube--a">A</div>
      <div className="ti-cube ti-cube--pi">π</div>
      <div className="ti-spark" aria-hidden="true">✦</div>
    </div>
  );
}

/** Major & career test: a signpost of majors on a graduation cap. */
export function ArtCareer() {
  const signs = [
    ["Engineering", "#1E5BFF"],
    ["Medicine", "#22A860"],
    ["Business", "#F5A623"],
    ["Design", "#8B5CF6"],
  ];
  return (
    <div className="ti-art" role="img" aria-label="A signpost of majors — engineering, medicine, business and design">
      <div className="ti-blob" aria-hidden="true"></div>
      <div className="ti-cap">
        <div className="ti-cap-top"></div>
        <div className="ti-cap-base"></div>
        <div className="ti-cap-tassel"></div>
      </div>
      <div className="ti-pole"></div>
      {signs.map(([l, c], i) => (
        <div
          key={l}
          className={`ti-sign ${i % 2 ? "ti-sign--l" : "ti-sign--r"}`}
          style={{ top: `${108 + i * 62}px`, background: c, animationDelay: `${0.3 + i * 0.18}s` }}
        >
          {l}
        </div>
      ))}
      <div className="ti-orbit ti-orbit--q">?</div>
      <div className="ti-orbit ti-orbit--compass">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" stroke="none" /></svg>
      </div>
      <div className="ti-spark" aria-hidden="true">✦</div>
    </div>
  );
}

/** IELTS: an opening book, skills rising, and a plane tracing its path. */
export function ArtIelts() {
  return (
    <div className="ti-art" role="img" aria-label="An open book with speaking, listening and reading skills rising beside it">
      <div className="ti-blob" aria-hidden="true"></div>
      <div className="ti-plane" aria-hidden="true">
        <svg viewBox="0 0 220 80" width="220" height="80" fill="none">
          <path className="ti-plane-path" d="M8 70 Q 90 8 208 22" stroke="var(--ulearn-blue-100)" strokeWidth="2" strokeDasharray="2 8" strokeLinecap="round" />
          <g className="ti-plane-jet"><path d="M0 0 L15 5.5 L0 11 L3.8 5.5 Z" fill="var(--brand)" /></g>
        </svg>
      </div>
      <div className="ti-book">
        <div className="ti-book-spine"></div>
        <div className="ti-book-page ti-book-page--l"><span></span><span></span><span></span><span style={{ width: "55%" }}></span></div>
        <div className="ti-book-page ti-book-page--r"><span></span><span style={{ width: "70%" }}></span><span></span><span style={{ width: "45%" }}></span></div>
      </div>
      {[["Speaking", "#1E5BFF", 0], ["Listening", "#E4457E", 1], ["Reading", "#22A860", 2]].map(([l, c, i]) => (
        <div key={l} className="ti-skill" style={{ "--i": i, background: c }}>{l}</div>
      ))}
      <div className="ti-flags">
        <img className="f-ielts" src={IeltsLogo} alt="IELTS" />
        <span className="f-kw"><i></i><i></i><i></i></span>
      </div>
      <div className="ti-spark" aria-hidden="true">✦</div>
    </div>
  );
}

/** Apply: a university offer letter stamping itself ACCEPTED, plane overhead. */
export function ArtApply() {
  return (
    <div className="ti-art" role="img" aria-label="A university offer letter stamped accepted, with a plane flying overhead">
      <div className="ti-blob" aria-hidden="true"></div>
      <div className="ti-plane" aria-hidden="true">
        <svg viewBox="0 0 220 80" width="220" height="80" fill="none">
          <path className="ti-plane-path" d="M8 70 Q 90 8 208 22" stroke="var(--ulearn-blue-100)" strokeWidth="2" strokeDasharray="2 8" strokeLinecap="round" />
          <g className="ti-plane-jet"><path d="M0 0 L15 5.5 L0 11 L3.8 5.5 Z" fill="var(--brand)" /></g>
        </svg>
      </div>
      <div className="ti-sheet ti-accept">
        <div className="ti-sheet-head">
          <span className="ti-accept-crest" aria-hidden="true">Ü</span> University Offer
        </div>
        <div className="ti-accept-lines" aria-hidden="true">
          <span></span><span style={{ width: "72%" }}></span><span></span><span style={{ width: "58%" }}></span>
        </div>
        <div className="ti-accept-stamp">
          ACCEPTED
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        </div>
      </div>
      <div className="ti-medal">98%</div>
      <div className="ti-cube ti-cube--a">🇬🇧</div>
      <div className="ti-cube ti-cube--pi">🎓</div>
      <div className="ti-spark" aria-hidden="true">✦</div>
    </div>
  );
}

/** MOHE checker: the ministry seal over a scholarship letter, meter rising. */
export function ArtMohe() {
  return (
    <div className="ti-art" role="img" aria-label="A MOHE scholarship letter with the Kuwait emblem and a rising percentage meter">
      <div className="ti-blob" aria-hidden="true"></div>
      <div className="ti-sheet ti-accept">
        <div className="ti-sheet-head">
          <img src="/design/tests-tools/mohe-emblem.png" alt="" style={{ height: 26, width: "auto" }} /> بعثة دراسية
        </div>
        <div className="ti-accept-lines" aria-hidden="true">
          <span></span><span style={{ width: "72%" }}></span><span></span><span style={{ width: "58%" }}></span>
        </div>
        <div className="ti-accept-stamp">
          مقبول
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        </div>
      </div>
      <div className="ti-medal">96%</div>
      <div className="ti-cube ti-cube--a">🇰🇼</div>
      <div className="ti-cube ti-cube--pi">🇬🇧</div>
      <div className="ti-spark" aria-hidden="true">✦</div>
    </div>
  );
}

/** Cost calculator: a budget sheet totting itself up, coins floating by. */
export function ArtCost() {
  return (
    <div className="ti-art" role="img" aria-label="A monthly budget sheet with rising bars and a KWD total badge">
      <div className="ti-blob" aria-hidden="true"></div>
      <div className="ti-sheet ti-accept">
        <div className="ti-sheet-head">
          <span className="ti-accept-crest" aria-hidden="true">£</span> Monthly budget
        </div>
        <div className="ti-accept-lines" aria-hidden="true">
          <span></span><span style={{ width: "64%" }}></span><span style={{ width: "80%" }}></span><span style={{ width: "48%" }}></span>
        </div>
        <div className="ti-accept-stamp">
          KWD ✓
        </div>
      </div>
      <div className="ti-medal">د.ك</div>
      <div className="ti-cube ti-cube--a">🏠</div>
      <div className="ti-cube ti-cube--pi">🍔</div>
      <div className="ti-spark" aria-hidden="true">✦</div>
    </div>
  );
}

/** Uni matcher: a shortlist card checking itself off, campus cubes floating. */
export function ArtMatch() {
  return (
    <div className="ti-art" role="img" aria-label="A ranked university shortlist with a 96% match badge">
      <div className="ti-blob" aria-hidden="true"></div>
      <div className="ti-sheet ti-accept">
        <div className="ti-sheet-head">
          <span className="ti-accept-crest" aria-hidden="true">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" /></svg>
          </span> My shortlist
        </div>
        <div className="ti-accept-lines" aria-hidden="true">
          <span></span><span style={{ width: "68%" }}></span><span style={{ width: "78%" }}></span><span style={{ width: "52%" }}></span>
        </div>
        <div className="ti-accept-stamp">
          MATCH
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        </div>
      </div>
      <div className="ti-medal">96%</div>
      <div className="ti-cube ti-cube--a">🎓</div>
      <div className="ti-cube ti-cube--pi">🏛️</div>
      <div className="ti-spark" aria-hidden="true">✦</div>
    </div>
  );
}

/** Accommodation finder: a residence checking itself off the shortlist. */
export function ArtHome() {
  return (
    <div className="ti-art" role="img" aria-label="A student residence shortlist with a key badge and a booked stamp">
      <div className="ti-blob" aria-hidden="true"></div>
      <div className="ti-sheet ti-accept">
        <div className="ti-sheet-head">
          <span className="ti-accept-crest" aria-hidden="true">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 9h1M14 9h1M9 13h1M14 13h1" /><path d="M10 21v-4h4v4" /></svg>
          </span> My residence
        </div>
        <div className="ti-accept-lines" aria-hidden="true">
          <span></span><span style={{ width: "66%" }}></span><span style={{ width: "80%" }}></span><span style={{ width: "50%" }}></span>
        </div>
        <div className="ti-accept-stamp">
          BOOKED
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        </div>
      </div>
      <div className="ti-medal">🔑</div>
      <div className="ti-cube ti-cube--a">🏠</div>
      <div className="ti-cube ti-cube--pi">🛏️</div>
      <div className="ti-spark" aria-hidden="true">✦</div>
    </div>
  );
}

const ARTS = { mock: ArtMock, career: ArtCareer, ielts: ArtIelts, apply: ArtApply, mohe: ArtMohe, cost: ArtCost, match: ArtMatch, home: ArtHome };

/** @param {{kind: 'mock'|'career'|'ielts'|'apply'|'mohe'|'cost'|'match'|'home'}} props */
export default function TestArt({ kind = "mock" }) {
  const Art = ARTS[kind] || ArtMock;
  return <Art />;
}
